// src/features/bookings/components/BookingList/index.jsx
// ✅ عرض الحجوزات — بطاقات وجدول وتقويم

import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useReactToPrint } from 'react-to-print';

import { useAuth }        from '../../../auth/context/AuthContext';
import useBookings        from '../../hooks/useBookings';

import PageHeader         from '../../../../shared/components/layout/PageHeader';
import Button             from '../../../../shared/components/ui/Button';
import LoadingSpinner     from '../../../../shared/components/ui/LoadingSpinner';
import EmptyState         from '../../../../shared/components/ui/EmptyState';

import BookingFilters     from './BookingFilters';
import BookingSortBar     from './BookingSortBar';
import BookingStats       from './BookingStats';
import BookingCard        from './BookingCard';
import BookingTable       from './BookingTable';
import AdvancedCalendar   from './AdvancedCalendar';
import BookingForm        from '../BookingForm';

import PrintableBookings  from '../../../../shared/components/print/PrintableBookings';

import styles             from './BookingList.module.css';

// ── مكوّن Pagination ─────────────────────────────────────────
const Pagination = ({ currentPage, totalPages, totalCount, pageSize, onPageChange, loading }) => {
  if (totalPages <= 1) return null;

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem   = Math.min(currentPage * pageSize, totalCount);

  const getPageNumbers = () => {
    const delta = 2;
    const pages = [];
    const left  = Math.max(2, currentPage - delta);
    const right = Math.min(totalPages - 1, currentPage + delta);

    pages.push(1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push('...');
    if (totalPages > 1) pages.push(totalPages);
    return pages;
  };

  return (
    <div className={styles.pagination}>
      <span className={styles.paginationInfo}>
        {startItem}–{endItem} من {totalCount} حجز
      </span>
      <div className={styles.paginationControls}>
        <button
          className={styles.pageBtn}
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1 || loading}
        >
          ›
        </button>
        {getPageNumbers().map((page, i) =>
          page === '...' ? (
            <span key={`d${i}`} className={styles.pageDots}>…</span>
          ) : (
            <button
              key={page}
              className={`${styles.pageBtn} ${page === currentPage ? styles.pageBtnActive : ''}`}
              onClick={() => onPageChange(page)}
              disabled={loading}
            >
              {page}
            </button>
          )
        )}
        <button
          className={styles.pageBtn}
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages || loading}
        >
          ‹
        </button>
      </div>
    </div>
  );
};

// ── المكوّن الرئيسي ───────────────────────────────────────────
const BookingList = () => {
  const { hasPermission, loggedInUser } = useAuth();
  const printableRef = useRef(null);

  // ✅ أوضاع العرض: بطاقات / جدول / تقويم
  const [viewMode,    setViewMode]    = useState('grid');
  const [showForm,    setShowForm]    = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const {
    bookings, statistics, loading, error,
    filters, handleFilterChange, clearFilters,
    sortConfig, handleSort,
    loadBookings, changeBookingStatus,
    currentPage, totalPages, totalCount, pageSize, goToPage,
  } = useBookings();

  useEffect(() => {
    loadBookings(1);
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const allowedBookingTypes = useMemo(() => {
    if (!loggedInUser) return [];
    if (loggedInUser.is_admin) return [];
    return loggedInUser.departments?.map((d) => ({
      value: d.associated_booking_type,
      label: d.name,
    })) ?? [];
  }, [loggedInUser]);

  const handlePrint = useReactToPrint({
    contentRef: printableRef,
    documentTitle: `Bookings-${new Date().toISOString().slice(0, 10)}`,
  });

  const handleBookingCreated = () => {
    setShowForm(false);
    loadBookings(1);
  };

  // ─── العرض ──────────────────────────────────────────────────
  const renderContent = () => {
    if (loading && bookings.length === 0) {
      return <LoadingSpinner message="جاري تحميل الحجوزات..." />;
    }
    if (error) {
      return <EmptyState icon="⚠️" title="حدث خطأ" message={error} />;
    }
    if (viewMode === 'calendar') {
      return (
        <AdvancedCalendar
          filters={filters}
          onStatusUpdate={changeBookingStatus}
        />
      );
    }
    if (!loading && bookings.length === 0) {
      return (
        <EmptyState
          title="لا توجد حجوزات"
          message="جرّب تعديل الفلاتر أو أضف حجزاً جديداً."
          actionLabel={hasPermission('add_booking') ? '+ إضافة حجز' : undefined}
          onAction={() => setShowForm(true)}
        />
      );
    }
    if (viewMode === 'table') {
      return (
        <BookingTable
          bookings={bookings}
          sortConfig={sortConfig}
          onSort={handleSort}
          loading={loading}
        />
      );
    }
    return (
      <div className={styles.grid}>
        {bookings.map((booking, i) => (
          <BookingCard
            key={booking.id}
            booking={booking}
            onStatusUpdate={changeBookingStatus}
            sequenceNumber={(currentPage - 1) * pageSize + i + 1}
          />
        ))}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div style={{ display: 'none' }}>
        <PrintableBookings ref={printableRef} bookings={bookings} filters={filters} />
      </div>

      {/* ── الهيدر ── */}
      <PageHeader
        title={`سجل الحجوزات (${totalCount})`}
        actions={
          <>
            {hasPermission('add_booking') && (
              <Button
                variant={showForm ? 'danger' : 'primary'}
                onClick={() => setShowForm(!showForm)}
              >
                {showForm ? 'إغلاق النموذج' : '+ إضافة حجز'}
              </Button>
            )}
            <Button variant="secondary" onClick={() => setShowFilters(!showFilters)}>
              {showFilters ? 'إخفاء الفلاتر' : 'الفلاتر'}
            </Button>
            <Button
              variant="info"
              onClick={handlePrint}
              disabled={loading || bookings.length === 0}
            >
              طباعة PDF
            </Button>
          </>
        }
      />

      {/* ── الإحصائيات — أعلى مباشرة ── */}
      {statistics && <BookingStats statistics={statistics} />}

      {/* ── شريط الأدوات ── */}
      <div className={styles.toolbar}>
        {/* تبديل العرض: بطاقات / جدول / تقويم */}
        <div className={styles.viewToggle}>
          <button
            className={`${styles.toggleBtn} ${viewMode === 'grid'  ? styles.activeToggle : ''}`}
            onClick={() => setViewMode('grid')}
          >
            بطاقات
          </button>
          <button
            className={`${styles.toggleBtn} ${viewMode === 'table' ? styles.activeToggle : ''}`}
            onClick={() => setViewMode('table')}
          >
            جدول
          </button>
          <button
            className={`${styles.toggleBtn} ${viewMode === 'calendar' ? styles.activeToggle : ''}`}
            onClick={() => setViewMode('calendar')}
          >
            تقويم
          </button>
        </div>

        {viewMode !== 'calendar' && totalPages > 1 && (
          <span className={styles.pageIndicator}>
            صفحة {currentPage} / {totalPages}
          </span>
        )}
      </div>

      {/* ── شريط الفرز ── */}
      {viewMode !== 'calendar' && (
        <BookingSortBar sortConfig={sortConfig} onSort={handleSort} />
      )}

      {/* ── الفلاتر ── */}
      {showFilters && (
        <BookingFilters
          filters={filters}
          onChange={handleFilterChange}
          onClear={clearFilters}
          allowedBookingTypes={allowedBookingTypes}
        />
      )}

      {/* ── نموذج الإضافة ── */}
      {showForm && <BookingForm onBookingCreated={handleBookingCreated} />}

      {/* ── المحتوى ── */}
      {renderContent()}

      {/* ── Pagination ── */}
      {viewMode !== 'calendar' && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          onPageChange={goToPage}
          loading={loading}
        />
      )}
    </div>
  );
};

export default BookingList;
